import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, uploadApi } from "../../lib/api";

const initialForm = {
  full_name: "",
  title: "",
  department_name: "",
  position: "",
  email: "",
  phone: "",
  office: "",
  biography: "",
  research_interests: "",
  qualifications: "",
  status: "active",
  photo: null,
};

export default function AdminStaffForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [existingPhoto, setExistingPhoto] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api(`/api/admin/staff/${id}`)
      .then((person) => {
        setForm({
          full_name: person.full_name || "",
          title: person.title || "",
          department_name: person.department_name || "",
          position: person.position || "",
          email: person.email || "",
          phone: person.phone || "",
          office: person.office || "",
          biography: person.biography || "",
          research_interests: person.research_interests || "",
          qualifications: person.qualifications || "",
          status: person.status || "active",
          photo: null,
        });
        setExistingPhoto(person.photo_url || "");
      })
      .catch((err) => setError(err.message));
  }, [id]);

  function updateField(e) {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);

    const fd = new FormData();
    for (const key of ["full_name", "title", "department_name", "position", "email", "phone", "office", "biography", "research_interests", "qualifications", "status"]) {
      fd.append(key, form[key] || "");
    }
    if (form.photo) fd.append("photo", form.photo);

    try {
      await uploadApi(id ? `/api/admin/staff/${id}` : "/api/admin/staff", fd, id ? "PUT" : "POST");
      navigate("/adxm/staff");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={id ? "Edit Staff Profile" : "Add Staff Profile"}
      subtitle="Create rich staff profiles with department, office, contact, biography and research information."
      actions={<Link to="/adxm/staff" className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700">Back to Staff</Link>}
    >
      <form onSubmit={submit} className="mx-auto max-w-5xl rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        {error && <div className="mb-5 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        <div className="grid gap-5 md:grid-cols-3">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Title</span>
            <input name="title" value={form.title} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" placeholder="Dr., Engr., Mr., Mrs." />
          </label>
          <label className="block md:col-span-2">
            <span className="text-sm font-bold text-slate-700">Full Name</span>
            <input name="full_name" value={form.full_name} onChange={updateField} required className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Department / Unit</span>
            <input name="department_name" value={form.department_name} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Position</span>
            <input name="position" value={form.position} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" placeholder="Lecturer, HOD, Director, Registrar..." />
          </label>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Email</span>
            <input name="email" type="email" value={form.email} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Phone</span>
            <input name="phone" value={form.phone} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Office</span>
            <input name="office" value={form.office} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Qualifications</span>
            <textarea name="qualifications" value={form.qualifications} onChange={updateField} rows="4" className="mt-2 w-full rounded-xl border border-slate-300 p-3" placeholder="PhD, MSc, BSc, professional certifications..." />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Research Interests</span>
            <textarea name="research_interests" value={form.research_interests} onChange={updateField} rows="4" className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
        </div>

        <label className="mt-5 block">
          <span className="text-sm font-bold text-slate-700">Biography</span>
          <textarea name="biography" value={form.biography} onChange={updateField} rows="6" className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
        </label>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Status</span>
            <select name="status" value={form.status} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Photo {id ? "(leave empty to keep existing)" : ""}</span>
            <input name="photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
        </div>

        {existingPhoto && <img src={existingPhoto} alt="Current staff" className="mt-4 h-28 w-28 rounded-2xl object-cover" />}

        <button disabled={saving} className="mt-8 rounded-xl bg-emerald-700 px-6 py-3 font-bold text-white disabled:opacity-60">
          {saving ? "Saving..." : "Save Staff Profile"}
        </button>
      </form>
    </AdminLayout>
  );
}
